import Num, { inputFormatter } from '@flipster/utils/num';

import type { ModalBasisMode } from '../../calculator/tpsl-basis';

export type FormatSignedBasisSecondaryOptions = {
  /** When true (default), prefix `+` for positive non-zero. Modal / inline signed use false. */
  withPlus?: boolean;
};

export type SecondarySign = 'positive' | 'negative';

/**
 * Display formatter for Change / ROI / P&L secondary:
 * - positive → `+10.00` (or `10.00` when `withPlus: false`)
 * - negative → `-10.00`
 * - zero → `0.00` (no `+`)
 * Absolute price fields must not use this.
 */
export function formatSignedBasisSecondaryDisplay(
  value: string,
  precision: number,
  options?: FormatSignedBasisSecondaryOptions,
): string {
  if (value === '' || value == null) return '';
  if (value === '-') return '-';

  const showPlus = options?.withPlus !== false;
  const isNegative = value.startsWith('-');
  const body = value.replace(/^[+-]/, '');
  const formattedBody = inputFormatter(body, { withComma: true, precision });
  if (!formattedBody) return isNegative ? '-' : '';

  const forNum = body.endsWith('.') ? body.slice(0, -1) || '0' : body;
  if (forNum === '') return isNegative ? `-${formattedBody}` : formattedBody;

  const n = Num(forNum);
  if (!n.isFinite()) return isNegative ? `-${formattedBody}` : formattedBody;
  if (n.isZero()) return formattedBody;

  if (isNegative) {
    return formattedBody.startsWith('-') ? formattedBody : `-${formattedBody}`;
  }
  return showPlus ? `+${formattedBody}` : formattedBody;
}

/**
 * Storage/calc deformatter: keep leading `-`, drop `+` and commas.
 * `Num` / basis converters accept the result.
 */
export function deformatSignedBasisSecondary(value: string): string {
  const noComma = value.replace(/,/g, '');
  const match = noComma.match(/^([+-])?(.*)$/);
  if (!match) return '';
  const sign = match[1] === '-' ? '-' : '';
  const body = (match[2] ?? '').replace(/[^\d.]/g, '');
  const [integer = '', ...rest] = body.split('.');
  const normalized = rest.length > 0 ? `${integer}.${rest.join('')}` : integer;
  return `${sign}${normalized}`;
}

/**
 * Inline Order Form ROI/PnL typing normalize (Binance-aligned):
 * - TP: unsigned magnitude (`10`)
 * - SL: force leading `-` (`10` → `-10`); bare `-` / empty → `''`
 * User does not type `+/-`; callers use digit/signed input accordingly.
 */
export function normalizeInlineRoiPnlSecondary(leg: 'tp' | 'sl', value: string): string {
  const cleaned = deformatSignedBasisSecondary(value);
  if (cleaned === '' || cleaned === '-') return '';

  const body = cleaned.startsWith('-') ? cleaned.slice(1) : cleaned;
  if (body === '') return '';

  if (leg === 'tp') return body;
  return `-${body}`;
}

/** Expected secondary sign for modal Change / ROI / P&L (side + leg). */
export function getExpectedModalSecondarySign({
  mode,
  leg,
  side,
}: {
  mode: ModalBasisMode;
  leg: 'tp' | 'sl';
  side: 'Long' | 'Short';
}): SecondarySign {
  if (mode === 'change') {
    // Long TP / Short SL → price up (+); Long SL / Short TP → price down (−)
    const priceUp = (side === 'Long') === (leg === 'tp');
    return priceUp ? 'positive' : 'negative';
  }
  // ROI / PnL: profit (+) for TP, loss (−) for SL — both sides
  return leg === 'tp' ? 'positive' : 'negative';
}

/** True when value has a non-zero sign opposite to expected (empty / zero → false). */
export function isOppositeSecondarySign(expected: SecondarySign, value: string): boolean {
  const cleaned = deformatSignedBasisSecondary(value);
  if (cleaned === '' || cleaned === '-') return false;
  const n = Num(cleaned);
  if (!n.isFinite() || n.isZero()) return false;
  return expected === 'positive' ? n.isNegative() : n.isPositive();
}

/**
 * Modal typing (after-clear policy):
 * - While editing an opposite (corrupted) non-empty value, keep user edits as-is.
 * - After full clear (empty), force expected sign on the next input.
 */
export function normalizeModalSecondaryByExpectedSign(
  expected: SecondarySign,
  nextValue: string,
  previousValue: string,
): string {
  const next = deformatSignedBasisSecondary(nextValue);
  const prev = deformatSignedBasisSecondary(previousValue);
  const prevEmpty = prev === '' || prev === '-';
  const prevOpposite = isOppositeSecondarySign(expected, prev);

  if (prevOpposite && !prevEmpty && next !== '' && next !== '-') {
    return next;
  }

  // Keep lone `-` while typing (set on focus for expected-negative); empty stays empty until focus.
  if (next === '') return '';
  if (next === '-') return expected === 'negative' ? '-' : '';

  const body = next.startsWith('-') ? next.slice(1) : next;
  if (body === '') return expected === 'negative' ? '-' : '';
  return expected === 'negative' ? `-${body}` : body;
}
